import { useMemo } from "react";
import { ResourceTypeLink, WorkspaceLimitsDc } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { useNotification } from "./useNotification";
import { createNotificationLink } from "./createNotificationLink";
import { useCheckLimits } from "../useCheckLimits";
import { isInfinityLimit } from "../useLimitsExceededCheck";

const getResourceCounts = (resourceType: ResourceTypeLink, limits?: WorkspaceLimitsDc) => {
  if (!limits) return {};

  switch (resourceType) {
    case ResourceTypeLink.Layer:
      return {
        resourceCount: limits.currentServiceCount,
        maxCount: limits.maxServicesCount,
      };
    case ResourceTypeLink.Table:
      return {
        resourceCount: limits.currentTableCount,
        maxCount: limits.maxTablesCount,
      };
    case ResourceTypeLink.Project:
      return {
        resourceCount: limits.currentProjectCount,
        maxCount: limits.maxProjectsCount,
      };
    default:
  }
};

export const useLimitResourceCount = <Trigger>(resourceType: ResourceTypeLink, trigger?: Trigger) => {
  const { t } = useTranslation("dialogs");
  const { limits } = useCheckLimits();
  const { resourceCount, maxCount } = getResourceCounts(resourceType, limits);
  const limitIndent = 5;

  const resourceDescription: Record<ResourceTypeLink, string> = useMemo(
    () => ({
      projects: t("limitsExceeded.ofMaps"),
      layers: t("limitsExceeded.ofLayers"),
      tables: t("limitsExceeded.ofTables"),
    }),
    [t],
  );

  const isLimitClose = isInfinityLimit(maxCount)
    ? false
    : !!maxCount && resourceCount && maxCount - resourceCount <= limitIndent;

  useNotification(trigger && isLimitClose, {
    warning: true,
    title: t("limitsExceeded.thereMightBeAProblem"),
    description: createNotificationLink(
      `${t("limitsExceeded.youHaveCreated")} ${resourceCount} ${t("limitsExceeded.of")} ${maxCount} ${t(
        "limitsExceeded.ofAvailable",
      )} ${resourceDescription[resourceType]}.`,
      {
        title: t("limitsExceeded.details"),
        href: `${location.origin}/agreements/accessible-resources`,
        target: "_blank",
      },
    ),
    duration: 6000,
  });
};
